
import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formbold.com/s/cosmeticaproshop-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as Record<string, string>).toString(),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section id="contacto" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h2 className="text-3xl font-serif font-bold text-stone-900 mb-6">
              CONTACTO
            </h2>
            <p className="text-stone-600 mb-8 leading-relaxed">
              ¿Tienes alguna pregunta? Estamos aquí para ayudarte. Contáctanos y te responderemos lo antes posible.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-lg">
                  <i className="ri-map-pin-line text-stone-700 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 mb-1">Dirección</h4>
                  <p className="text-stone-600 text-sm">
                    Calle Hermanos Villalonga 5<br />
                    Bajo Derecha, 46020<br />
                    Valencia, España
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-lg">
                  <i className="ri-mail-line text-stone-700 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 mb-1">Email</h4>
                  <a href="mailto:cosmeticaproshop@gmail.com" className="text-stone-600 text-sm hover:text-stone-900 transition-colors">
                    cosmeticaproshop@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-lg">
                  <i className="ri-time-line text-stone-700 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 mb-1">Horario</h4>
                  <p className="text-stone-600 text-sm">
                    Lunes - Viernes: 10:00 - 19:00<br />
                    Sábado: 10:00 - 14:00
                  </p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8 rounded-xl overflow-hidden h-[200px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3079.8!2d-0.38!3d39.47!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDI4JzEyLjAiTiAwwrAyMic0OC4wIlc!5e0!3m2!1ses!2ses!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación Cosmetica Pro Shop"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-stone-50 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-stone-900 mb-6">Envíanos un mensaje</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Nombre</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="Tu nombre"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="tu@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="+34 600 000 000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Mensaje</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="¿En qué podemos ayudarte?"
                  required
                ></textarea>
                <p className="text-xs text-stone-400 mt-1">{formData.message.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-stone-900 text-white font-medium hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar mensaje'}
              </button>

              {submitStatus === 'success' && (
                <p className="text-green-600 text-sm text-center">¡Mensaje enviado correctamente!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-sm text-center">Error al enviar. Inténtalo de nuevo.</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
